<script setup lang="ts">
import { MapPin, Phone, Clock, Crown } from 'lucide-vue-next'
import { usePhone } from '~/composables/usePhone'
import { useEntityBranch } from '~/composables/useEntityBranch'
import type { IBranchCard } from '~/components/entity/branch'
import { EntityOrganizationLogo } from '~/components/entity/organization-logo'

interface Props {
  item: IBranchCard
  hideOrganizationDetails?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  hideOrganizationDetails: false
})

const { formatPhoneForTel } = usePhone()
const {
  getTypeLabels,
  getOrganizationTypeLabel,
  formatAddress,
  formatOpeningHours,
  getBranchTitle,
  getServicesWithIcons
} = useEntityBranch()

const services = computed(() => getServicesWithIcons(props.item.services))
</script>

<template>
  <article class="Card" :class="{ 'ring-2 ring-blue-600': props.item.highlight }">
    <div class="flex flex-col space-y-3">
      <div>
        <!-- Branch Title -->
        <div class="mb-3 flex items-start gap-4">
          <EntityOrganizationLogo
            v-if="!props.hideOrganizationDetails"
            :src="props.item.organization.logo"
            :alt="props.item.organization.title"
            :to="props.item.organization.url"
            :aria-label="`${$t('common.more_about')} ${props.item.organization.title}`"
            size="lg"
            class="shrink-0"
          />
          <Heading tag="h3" class="h5 flex items-center gap-2 font-serif font-semibold text-gray-900">
            <Crown v-if="props.item.is_head" class="h-5 w-5 shrink-0 text-blue-600 md:-mt-1 md:h-6 md:w-6" />
            {{ getBranchTitle(props.item) }}
          </Heading>
        </div>

        <div class="mt-2 flex flex-col gap-4 md:flex-row">
          <!-- Left Column: Types, Address, Phones, and Services -->
          <div class="flex-1 space-y-3">
            <!-- Types -->
            <div class="flex flex-wrap gap-1">
              <Badge v-if="props.item.organization_type" variant="blue" rounded="full">
                {{ getOrganizationTypeLabel(props.item.organization_type) }}
              </Badge>
              <Badge
                v-for="typeLabel in getTypeLabels(props.item.types)"
                :key="typeLabel"
                variant="blue"
                rounded="full"
              >
                {{ typeLabel }}
              </Badge>
            </div>

            <!-- Address and Phones -->
            <div class="space-y-2 text-sm text-gray-600">
              <div v-if="props.item.address_formatted" class="flex items-start gap-2">
                <MapPin class="mt-0.5 h-4 w-4 min-w-4 text-gray-400" />
                <span>{{ formatAddress(props.item.address_formatted) }}</span>
              </div>

              <div v-if="props.item.phones?.length" class="flex items-start gap-2">
                <Phone class="mt-0.5 h-4 w-4 min-w-4 text-gray-400" />
                <span>
                  <template v-for="(phone, index) in props.item.phones" :key="phone">
                    <a
                      :href="`tel:${formatPhoneForTel(phone)}`"
                      class="text-blue-600 hover:underline"
                      >{{ phone }}</a
                    >
                    <span v-if="index < props.item.phones.length - 1">, </span>
                  </template>
                </span>
              </div>
            </div>

            <!-- Services -->
            <div v-if="services.length">
              <div class="flex flex-wrap gap-2">
                <Badge
                  v-for="service in services"
                  :key="service.key"
                  variant="blue"
                  rounded="full"
                  class="flex items-center gap-1.5 bg-blue-50 font-normal text-gray-700"
                >
                  <component :is="service.icon" class="h-3 w-3" />
                  {{ service.label }}
                </Badge>
              </div>
            </div>
          </div>

          <!-- Right Column: Opening Hours -->
          <div v-if="props.item.opening_hours_formatted" class="flex-shrink-0 md:w-64">
            <div class="flex items-start gap-2 whitespace-pre-line text-sm text-gray-600">
              <Clock class="mt-0.5 h-4 w-4 min-w-4 text-gray-400" />
              <span>{{ formatOpeningHours(props.item.opening_hours_formatted) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </article>
</template>
